
import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Company, User } from '../types';
import { api } from '../services/apiService';

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

interface Session {
  login: string;
  logout: string;
  duration: number;
  type: string;
}

interface UserStat {
  user_id: string;
  user_name: string;
  total_seconds: number;
  session_count: number;
  sessions: Session[];
}

interface ConnectionTimeTabProps {
  company: Company;
  users: User[];
  onCompanyUpdate: (c: Company) => void;
  onUserUpdate?: (u: User) => void;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

function formatDateTime(dt: string): string {
  return new Date(dt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function barWidth(val: number, max: number): string {
  if (max === 0) return '0%';
  return `${Math.round((val / max) * 100)}%`;
}

const PRESET_MINUTES = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const ConnectionTimeTab: React.FC<ConnectionTimeTabProps> = ({ company, users, onCompanyUpdate, onUserUpdate }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';

  const [dateFrom, setDateFrom] = useState(firstOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [stats, setStats] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Timeout compagnie
  const [companyTimeout, setCompanyTimeout] = useState<number>(company.inactivityTimeout ?? 10);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyMsg, setCompanyMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Timeout par utilisateur
  const [userTimeouts, setUserTimeouts] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    users.forEach(u => { init[u.id] = u.inactivityTimeout !== undefined ? String(u.inactivityTimeout) : ''; });
    return init;
  });
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [userMsg, setUserMsg] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});

  const handleSaveCompanyTimeout = async () => {
    setSavingCompany(true);
    setCompanyMsg(null);
    try {
      const updated = await api.updateCompany(company.id, { inactivityTimeout: companyTimeout });
      onCompanyUpdate(updated);
      setCompanyMsg({ type: 'success', text: 'Délai mis à jour' });
    } catch {
      setCompanyMsg({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSavingCompany(false);
      setTimeout(() => setCompanyMsg(null), 3000);
    }
  };

  const handleSaveUserTimeout = async (userId: string) => {
    setSavingUserId(userId);
    const raw = userTimeouts[userId];
    const value = raw === '' ? null : parseInt(raw, 10);
    try {
      const updated = await api.updateUser(userId, { inactivityTimeout: value ?? undefined });
      if (onUserUpdate) onUserUpdate(updated);
      setUserMsg(prev => ({ ...prev, [userId]: { type: 'success', text: 'Mis à jour' } }));
    } catch {
      setUserMsg(prev => ({ ...prev, [userId]: { type: 'error', text: 'Erreur' } }));
    } finally {
      setSavingUserId(null);
      setTimeout(() => setUserMsg(prev => { const n = { ...prev }; delete n[userId]; return n; }), 3000);
    }
  };

  const fetchStats = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('orion_access_token') || localStorage.getItem('auth_token');
      const params = new URLSearchParams({ action: 'get_connection_stats', date_from: dateFrom, date_to: dateTo });
      const res = await fetch(`${API_BASE}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setStats([]); }
      else { setStats(data.stats || []); }
    } catch {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, [dateFrom, dateTo]);

  const maxSeconds = stats.length > 0 ? Math.max(...stats.map(s => s.total_seconds)) : 0;

  const applyTimeFormat = (ws: XLSX.WorkSheet, colIndex: number, fmt = '[h]:mm:ss') => {
    const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
    for (let r = 1; r <= range.e.r; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: colIndex });
      if (ws[addr]) { ws[addr].t = 'n'; ws[addr].z = fmt; }
    }
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // ── Feuille 1 : résumé par utilisateur ──
    // col 0:Utilisateur  1:Période  2:Sessions  3:Temps total (Excel time)
    const summaryRows = stats.map(u => ({
      'Utilisateur': u.user_name,
      'Période': `${dateFrom} → ${dateTo}`,
      'Sessions': u.session_count,
      'Temps total': u.total_seconds / 86400,
    }));
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 10 }, { wch: 14 }];
    applyTimeFormat(wsSummary, 3); // colonne D → Temps total
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Résumé');

    // ── Feuille 2 : détail sessions ──
    // col 0:Utilisateur  1:Connexion  2:Déconnexion  3:Durée (Excel time)  4:Type
    const detailRows = stats.flatMap(u =>
      u.sessions.map(s => ({
        'Utilisateur': u.user_name,
        'Connexion': formatDateTime(s.login),
        'Déconnexion': formatDateTime(s.logout),
        'Durée': s.duration / 86400,
        'Type': s.type === 'logout' ? 'Déconnexion' : 'Inactivité',
      }))
    );
    const wsDetail = XLSX.utils.json_to_sheet(detailRows);
    wsDetail['!cols'] = [{ wch: 24 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 14 }];
    applyTimeFormat(wsDetail, 3); // colonne D → Durée
    XLSX.utils.book_append_sheet(wb, wsDetail, 'Sessions');

    // ── Feuille par utilisateur ──
    stats.forEach(u => {
      const userRows = u.sessions.map(s => ({
        'Connexion': formatDateTime(s.login),
        'Déconnexion': formatDateTime(s.logout),
        'Durée': s.duration / 86400,
        'Type': s.type === 'logout' ? 'Déconnexion' : 'Inactivité',
      }));
      const wsUser = XLSX.utils.json_to_sheet(userRows);
      wsUser['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 14 }];
      applyTimeFormat(wsUser, 2); // colonne C → Durée
      // Les noms de feuilles Excel sont limités à 31 caractères et ne peuvent pas contenir: \ / ? * [ ]
      const sheetName = u.user_name.replace(/[\\/?*[\]]/g, '').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, wsUser, sheetName);
    });

    XLSX.writeFile(wb, `temps-connexion_${dateFrom}_${dateTo}.xlsx`);
  };

  return (
    <div className="p-10 animate-in fade-in space-y-10">

      {/* ── Section : Déconnexion automatique ── */}
      <div>
        <h3 className="ct-title text-2xl font-extrabold mb-1 tracking-tight">Déconnexion automatique</h3>
        <p className="ct-subtitle text-sm mb-8">Configurez le délai d'inactivité avant déconnexion automatique. Le réglage par utilisateur est prioritaire sur le défaut de la compagnie.</p>

        {/* Délai par défaut de la compagnie */}
        <div className="ct-section-card p-6 rounded-2xl mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="ct-user-name text-sm font-black uppercase tracking-wide">Délai par défaut (compagnie)</div>
              <div className="ct-label text-xs mt-0.5">Appliqué à tous les utilisateurs sans réglage spécifique</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-black text-indigo-400">{companyTimeout} min</span>
            </div>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2 mb-4">
            {PRESET_MINUTES.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setCompanyTimeout(m)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  companyTimeout === m
                    ? 'bg-indigo-600 text-white border border-indigo-600 shadow'
                    : 'ct-preset-btn'
                }`}
              >
                {m >= 60 ? `${m / 60}h` : `${m} min`}
              </button>
            ))}
            {/* Saisie libre */}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1}
                max={480}
                title="Délai personnalisé en minutes"
                placeholder="min"
                value={companyTimeout}
                onChange={e => setCompanyTimeout(Math.max(1, Math.min(480, parseInt(e.target.value) || 1)))}
                className="ct-input w-16 rounded-lg px-2 py-1.5 text-xs font-bold text-center"
              />
              <span className="ct-label text-xs">min</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveCompanyTimeout}
              disabled={savingCompany}
              className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-all disabled:opacity-60 shadow"
            >
              {savingCompany ? 'Sauvegarde…' : 'Enregistrer'}
            </button>
            {companyMsg && (
              <span className={`text-xs font-bold ${companyMsg.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                {companyMsg.text}
              </span>
            )}
          </div>
        </div>

        {/* Réglages par utilisateur */}
        {users.length > 0 && (
          <div className="ct-section-card rounded-2xl overflow-hidden">
            <div className="ct-section-header px-6 py-4">
              <div className="text-sm font-black uppercase tracking-wide">Réglage par utilisateur</div>
              <div className="ct-subtitle text-xs mt-0.5">Laisser vide pour utiliser le délai par défaut de la compagnie ({companyTimeout} min)</div>
            </div>
            <div className="ct-user-list">
              {users.map(u => (
                <div key={u.id} className="ct-user-row flex items-center gap-4 px-6 py-3">
                  <div className="ct-avatar-indigo w-8 h-8 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0">
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="ct-user-name text-sm font-bold truncate">{u.name}</div>
                    <div className="ct-user-email text-xs truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number"
                      min={1}
                      max={480}
                      value={userTimeouts[u.id] ?? ''}
                      onChange={e => setUserTimeouts(prev => ({ ...prev, [u.id]: e.target.value }))}
                      placeholder={`${companyTimeout} (défaut)`}
                      className="ct-input w-28 rounded-lg px-3 py-1.5 text-xs font-bold text-center"
                    />
                    <span className="ct-label text-xs">min</span>
                    <button
                      type="button"
                      onClick={() => handleSaveUserTimeout(u.id)}
                      disabled={savingUserId === u.id}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-all disabled:opacity-60"
                    >
                      {savingUserId === u.id ? '…' : 'OK'}
                    </button>
                    {userMsg[u.id] && (
                      <span className={`text-xs font-bold ${userMsg[u.id].type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {userMsg[u.id].text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Section : Historique de connexion ── */}
      <div>
        <h3 className="ct-title text-2xl font-extrabold mb-1 tracking-tight">Temps de connexion</h3>
        <p className="ct-subtitle text-sm mb-6">Durée de session par utilisateur sur une période donnée</p>

        {/* Filtres */}
        <div className="flex flex-wrap items-end gap-4 mb-8">
          <div className="flex flex-col gap-1">
            <label className="ct-label text-xs font-semibold uppercase tracking-wide">Du</label>
            <input
              type="date"
              title="Date de début"
              value={dateFrom}
              max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              className="ct-input rounded-xl px-4 py-2.5 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="ct-label text-xs font-semibold uppercase tracking-wide">Au</label>
            <input
              type="date"
              title="Date de fin"
              value={dateTo}
              min={dateFrom}
              onChange={e => setDateTo(e.target.value)}
              className="ct-input rounded-xl px-4 py-2.5 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={fetchStats}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold transition-all disabled:opacity-60 shadow"
          >
            {loading ? 'Chargement…' : 'Analyser'}
          </button>

          {stats.length > 0 && (
            <button
              type="button"
              onClick={handleExportExcel}
              className="ct-export-btn flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              Exporter Excel
            </button>
          )}

          <div className="flex gap-2 ml-auto flex-wrap">
            {[
              { label: "Aujourd'hui", from: today, to: today },
              { label: '7 jours', from: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10), to: today },
              { label: 'Ce mois', from: firstOfMonth, to: today },
            ].map(p => (
              <button
                type="button"
                key={p.label}
                onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                className="ct-shortcut-btn px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="ct-error-box mb-6 p-4 rounded-xl text-sm">{error}</div>
        )}

        {searched && !loading && stats.length === 0 && !error && (
          <div className="ct-empty-text text-center py-16 text-sm">
            Aucune donnée de connexion pour cette période.
          </div>
        )}

        {stats.length > 0 && (
          <div className="space-y-3">
            {stats.map(u => (
              <div key={u.user_id} className="ct-stat-row rounded-2xl overflow-hidden transition-shadow">
                <div
                  className="flex items-center gap-4 px-6 py-4 cursor-pointer select-none"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                >
                  <div className="ct-avatar-violet w-10 h-10 rounded-full flex items-center justify-center font-bold text-base flex-shrink-0">
                    {u.user_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="w-44 flex-shrink-0">
                    <div className="ct-stat-name font-semibold text-sm truncate">{u.user_name}</div>
                    <div className="ct-stat-sessions text-xs">{u.session_count} session{u.session_count !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="ct-bar-track h-2.5 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-indigo-400 rounded-full transition-all duration-500"
                        style={{ width: barWidth(u.total_seconds, maxSeconds) }}
                      />
                    </div>
                  </div>
                  <div className="w-24 text-right flex-shrink-0">
                    <span className="ct-stat-duration text-sm font-bold">{formatDuration(u.total_seconds)}</span>
                  </div>
                  <svg
                    className={`ct-chevron w-4 h-4 transition-transform flex-shrink-0 ${expandedUser === u.user_id ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {expandedUser === u.user_id && u.sessions.length > 0 && (
                  <div className="ct-table-area px-6 py-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="ct-table-head uppercase tracking-wide">
                          <th className="text-left py-1 pr-4 font-semibold">Connexion</th>
                          <th className="text-left py-1 pr-4 font-semibold">Déconnexion</th>
                          <th className="text-left py-1 pr-4 font-semibold">Durée</th>
                          <th className="text-left py-1 font-semibold">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {u.sessions.map((s, i) => (
                          <tr key={i} className="ct-table-row transition-colors">
                            <td className="ct-table-cell py-1.5 pr-4">{formatDateTime(s.login)}</td>
                            <td className="ct-table-cell py-1.5 pr-4">{formatDateTime(s.logout)}</td>
                            <td className="ct-table-cell-bold py-1.5 pr-4 font-semibold">{formatDuration(s.duration)}</td>
                            <td className="py-1.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                s.type === 'logout' ? 'ct-badge-logout' : 'ct-badge-inactivity'
                              }`}>
                                {s.type === 'logout' ? 'Déconnexion' : 'Inactivité'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {expandedUser === u.user_id && u.sessions.length === 0 && (
                  <div className="ct-table-area ct-empty-text px-6 py-4 text-xs">
                    Aucune session complète dans cette période (session peut-être encore active).
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionTimeTab;
